/**
 * colorhouse: newsletter form that posts straight to a Flodesk form (blocks/flodesk-signup.liquid),
 * ported from the Lovable site's NewsletterSignup.
 *
 * Flodesk doesn't send CORS headers, so the request goes out as no-cors and its response is opaque.
 * As on the Lovable site, a fetch that resolves counts as success; only a network failure shows an error.
 */
const EMAIL_PATTERN = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;

class FlodeskSignup extends HTMLElement {
  /** @type {HTMLFormElement | null} */
  form = null;

  /** @type {HTMLInputElement | null} */
  input = null;

  /** @type {HTMLButtonElement | null} */
  button = null;

  /** @type {HTMLElement | null} */
  status = null;

  connectedCallback() {
    this.form = this.querySelector('form');
    this.input = this.querySelector('input[type="email"]');
    this.button = this.querySelector('button[type="submit"]');
    this.status = this.querySelector('.flodesk-signup__status');
    this.form?.addEventListener('submit', this.#onSubmit);
  }

  disconnectedCallback() {
    this.form?.removeEventListener('submit', this.#onSubmit);
  }

  /** @param {SubmitEvent} event */
  #onSubmit = async (event) => {
    event.preventDefault();
    if (!this.input || !this.button) return;

    const email = this.input.value.trim();
    if (!EMAIL_PATTERN.test(email)) {
      this.#setStatus(this.dataset.invalidMessage, true);
      this.input.focus();
      return;
    }

    const label = this.button.textContent;
    this.button.disabled = true;
    this.button.textContent = this.dataset.loadingLabel ?? label;

    try {
      await fetch(`https://form.flodesk.com/forms/${this.dataset.formKey}/submit`, {
        method: 'POST',
        mode: 'no-cors',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ email }),
      });
      this.form?.reset();
      this.#setStatus(this.dataset.successMessage, false);
    } catch {
      this.#setStatus(this.dataset.errorMessage, true);
    } finally {
      this.button.disabled = false;
      this.button.textContent = label;
    }
  };

  /**
   * @param {string | undefined} message
   * @param {boolean} isError
   */
  #setStatus(message, isError) {
    if (!this.status || !this.input) return;
    this.status.textContent = message ?? '';
    this.status.classList.toggle('flodesk-signup__status--error', isError);
    if (isError) {
      this.input.setAttribute('aria-invalid', 'true');
    } else {
      this.input.removeAttribute('aria-invalid');
    }
  }
}

if (!customElements.get('flodesk-signup')) {
  customElements.define('flodesk-signup', FlodeskSignup);
}
